import { Modal } from '@lmt/unicorn-shared/app/components/common';
import { Button } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import {
  getAdminSettings,
  getSuperUserLoginForm,
  getSuperUserPasswordSetter,
} from '@lmt/unicorn-shared/app/components/DesktopUI';
import { isElectron } from '@lmt/unicorn-shared/app/helpers/common';
import { NetworkError } from '@lmt/unicorn-shared/app/helpers/error-handling';
import { useMatomoPageTracking } from '@lmt/unicorn-shared/app/helpers/matomo';
import { default as i18next } from 'i18next';
import { getBridge } from 'leo-manager/utils/bridge';
import React, { FC, useEffect, useState } from 'react';
import styles from './Launcher.module.scss';
import {
  addLogoutHandler,
  logOut,
  useRefreshTokensWhenPermissionsChange,
} from '@lmt/unicorn-shared/app/features/auth/operations';
import { useUniSelector } from '@lmt/unicorn-shared/app/store/types';
import { getSettings } from '@lmt/unicorn-shared/app/features/settings/selectors';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { useNavigate } from 'react-router';
import { getAxios } from '@lmt/shared-types';
import { useEventCallback } from '@mui/material';

export const Launcher: FC<
  React.PropsWithChildren<{
    backendStartFailed: boolean;
  }>
> = (props) => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | undefined>(undefined);
  const isNightTheme = useUniSelector(getSettings).isNightTheme;

  const [retry, setRetry] = useState(0);

  useRefreshTokensWhenPermissionsChange();
  useMatomoPageTracking();
  useEffect(() => {
    return addLogoutHandler(() => {
      navigate(pages.home.link(), { replace: true });
    });
  }, []);
  useEffect(() => {
    if (isNightTheme) {
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      document.documentElement.setAttribute('data-theme', 'light');
    }
  }, [isNightTheme]);

  const modals = useModal();

  useEffect(() => {
    if (props.backendStartFailed) {
      let cancelled = false;
      const showBackendError = async () => {
        const backendUrl = isElectron()
          ? (await getBridge().getSettings())?.backend.uri
          : undefined;
        const isLocalBackend =
          !!backendUrl &&
          ['localhost', '127.0.0.1', '[::1]'].includes(
            new URL(backendUrl).hostname,
          );
        const isBackendInstalled = await getBridge().getIsBackendInstalled();
        if (isLocalBackend && !isBackendInstalled) {
          // do not show the error if local backend is not installed
          return;
        }
        if (!cancelled) {
          await modals.showAlert({
            title: i18next.t('translation:home.backend_not_available_title'),
            text: i18next.t('translation:home.backend_not_available_text'),
          });
        }
      };
      void showBackendError().catch((e) =>
        console.error('Launcher.showBackendError', e),
      );
      return () => {
        cancelled = true;
      };
    }
  }, [props.backendStartFailed]);

  const SuperUserPasswordSetter = getSuperUserPasswordSetter();

  const [isSuperUserNeedSetting, setIsSuperUserNeedSetting] = useState(false);
  const [isSuperUserPasswordSetterOpen, setSuperUserPasswordSetterOpen] =
    useState(isSuperUserNeedSetting && !!SuperUserPasswordSetter);
  const [isSuperUserLoginOpen, setSuperUserLoginOpen] = useState(
    !isSuperUserNeedSetting && !isSuperUserPasswordSetterOpen,
  );
  const [isServerSettingsOpen, setServerSettingsOpen] = useState(
    !isSuperUserPasswordSetterOpen && !isSuperUserLoginOpen,
  );

  useEffect(() => {
    if (isElectron()) {
      const bridge = getBridge();
      bridge
        .getSuperUserSettings()
        .then((x) => {
          const superUserNeedSetting = !x.passwordHash && !x.password;
          const superUserPasswordSetterOpen =
            superUserNeedSetting && !!SuperUserPasswordSetter;
          const superUserLoginOpen =
            !superUserNeedSetting && !superUserPasswordSetterOpen;
          const serverSettingsOpen =
            !superUserPasswordSetterOpen && !superUserLoginOpen;

          setIsSuperUserNeedSetting(superUserNeedSetting);
          setSuperUserPasswordSetterOpen(superUserPasswordSetterOpen);
          setSuperUserLoginOpen(superUserLoginOpen);
          setServerSettingsOpen(serverSettingsOpen);
        })
        .catch((e) => console.error('Launcher.useEffect.isElectron', e));
    }
  }, []);

  const restartWindowIfBackendEnabled = useEventCallback(() => {
    const url = new URL(window.location.href);
    const backendUrl = url.searchParams.get('backend');
    if (!backendUrl) {
      return;
    }
    const requestUri = '/api';
    const checkBackendStatusAbortController = new AbortController();
    getAxios()
      .get(requestUri, {
        timeout: 15000,
        baseURL: backendUrl,
        signal: checkBackendStatusAbortController.signal,
      })
      .then((response) => {
        if (response.status === 200) {
          url.searchParams.delete('error');
          window.history.replaceState({}, '', url.toString());
          window.location.reload();
        }
      })
      .catch((error: Error) => console.error(error));
  });

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div>
          {error === NetworkError
            ? i18next.t('translation:home.network_error')
            : error}
        </div>
        <div className={styles.errorContainerButtons}>
          <Button
            title={i18next.t('translation:home.retry_button')}
            onClick={() => {
              setError(undefined);
              setRetry(retry + 1);
            }}
          />{' '}
          or{' '}
          <Button
            title={i18next.t('translation:load.log_out')}
            onClick={async () => {
              await logOut();
            }}
          />
        </div>
      </div>
    );
  }

  if (props.backendStartFailed) {
    const AdminSettings = getAdminSettings();
    const SuperUserLoginForm = getSuperUserLoginForm();
    return (
      <>
        {isSuperUserPasswordSetterOpen && (
          <Modal
            isOpen
            contentLabel={i18next.t('translation:home.super_user_settings')}
            onRequestClose={() => {}}
          >
            <SuperUserPasswordSetter
              onSuccess={() => {
                setSuperUserPasswordSetterOpen(false);
                setServerSettingsOpen(true);
              }}
            />
          </Modal>
        )}
        {isSuperUserLoginOpen && (
          <Modal
            isOpen
            contentLabel={i18next.t('translation:home.super_user_login')}
            onRequestClose={() => restartWindowIfBackendEnabled()}
          >
            <SuperUserLoginForm
              onSuccess={() => {
                setSuperUserLoginOpen(false);
                setServerSettingsOpen(true);
              }}
            />
          </Modal>
        )}
        {isServerSettingsOpen && (
          <Modal
            contentLabel={i18next.t('translation:home.admin_settings')}
            isOpen
            onRequestClose={() => restartWindowIfBackendEnabled()}
          >
            <AdminSettings backendStartFailed={true} />
          </Modal>
        )}
      </>
    );
  }
  return props.children;
};
